package fr.manyverse.app.presentation.components.character_sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.MenuBook
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.Whatshot
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacteristicEntry
import fr.manyverse.app.domain.model.GaugeEntry
import fr.manyverse.app.domain.model.PowerEntry
import fr.manyverse.app.domain.model.SkillEntry
import fr.manyverse.app.domain.validation.CharacterSheetValidator
import fr.manyverse.app.presentation.components.HsvColorPicker
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_sheet_add
import manyverse.shared.generated.resources.character_sheet_add_characteristic
import manyverse.shared.generated.resources.character_sheet_add_gauge
import manyverse.shared.generated.resources.character_sheet_add_power
import manyverse.shared.generated.resources.character_sheet_add_skill
import manyverse.shared.generated.resources.character_sheet_characteristic_name
import manyverse.shared.generated.resources.character_sheet_characteristic_value
import manyverse.shared.generated.resources.character_sheet_characteristics_section
import manyverse.shared.generated.resources.character_sheet_delete_entry
import manyverse.shared.generated.resources.character_sheet_entry_description
import manyverse.shared.generated.resources.character_sheet_gauge_color
import manyverse.shared.generated.resources.character_sheet_gauge_color_pick
import manyverse.shared.generated.resources.character_sheet_gauge_end
import manyverse.shared.generated.resources.character_sheet_gauge_increment
import manyverse.shared.generated.resources.character_sheet_gauge_initial
import manyverse.shared.generated.resources.character_sheet_gauge_name
import manyverse.shared.generated.resources.character_sheet_gauge_start
import manyverse.shared.generated.resources.character_sheet_gauges_section
import manyverse.shared.generated.resources.character_sheet_power_label
import manyverse.shared.generated.resources.character_sheet_powers_section
import manyverse.shared.generated.resources.character_sheet_skill_label
import manyverse.shared.generated.resources.character_sheet_skills_section
import org.jetbrains.compose.resources.stringResource
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Éditeur de fiche (jauges / caractéristiques / compétences / pouvoirs) **partagé** entre la fiche du joueur
 * ([fr.manyverse.app.presentation.components.player_screen.character.CharacterSheetTab]) et l'éditeur
 * de pré-tirés du MJ ([fr.manyverse.app.presentation.components.gm_screen.PreMadeCharacterFormDialog]).
 *
 * Principe : chaque bouton « Ajouter … » ajoute directement une ligne vide (mêmes champs que les autres)
 * sans validation par ligne. Les lignes se saisissent librement ; la conversion vers le domaine et la
 * validation n'ont lieu qu'à la demande via [CharacterSheetEditorState.validateAndBuild] (au clic sur
 * « Terminer » / « Enregistrer »).
 */

// --- Modèles de brouillon (état d'édition mutable, champs en String) ---

@OptIn(ExperimentalUuidApi::class)
class DraftGauge(val origin: GaugeEntry? = null, allowInitialEdit: Boolean = false) {
    val key: String = origin?.id ?: Uuid.random().toString()

    /**
     * La valeur initiale ne se saisit que sur les jauges en cours de création — sauf pour les
     * pré-tirés ([allowInitialEdit]), où le MJ peut aussi la réajuster sur une jauge existante.
     * Quand elle est masquée, la valeur de jeu en cours n'est jamais écrasée.
     */
    val showInitial: Boolean = allowInitialEdit || origin == null

    var label by mutableStateOf(origin?.label ?: "")
    var start by mutableStateOf(origin?.min?.toString() ?: "")
    var end by mutableStateOf(origin?.max?.toString() ?: "")
    var increment by mutableStateOf(origin?.increment?.toString() ?: "")
    var initial by mutableStateOf(if (showInitial) origin?.current?.toString().orEmpty() else "")
    var colorArgb by mutableStateOf(origin?.colorArgb)

    /** Vide si aucun champ significatif n'est renseigné (la couleur est ignorée). */
    fun isEmpty(): Boolean =
        label.isBlank() && start.isBlank() && end.isBlank() && increment.isBlank() &&
            initial.isBlank()

    private fun parsed(): Triple<Int, Int, Int>? {
        val min = start.toIntOrNull() ?: return null
        val max = end.toIntOrNull() ?: return null
        val inc = increment.toIntOrNull() ?: return null
        return Triple(min, max, inc)
    }

    /** `null` quand le champ est laissé vide ⇒ la jauge démarrera à sa valeur d'arrivée. */
    private fun parsedInitial(): Int? = initial.toIntOrNull()

    fun isValid(): Boolean {
        val (min, max, inc) = parsed() ?: return false
        if (initial.isNotBlank() && parsedInitial() == null) return false
        return CharacterSheetValidator.validateGauge(label, min, max, inc, parsedInitial()).isSuccess
    }

    fun toEntry(): GaugeEntry {
        val (min, max, inc) = parsed()!!
        val current = if (showInitial) {
            parsedInitial() ?: max
        } else {
            origin!!.current.coerceIn(min, max)
        }
        return origin?.copy(
            label = label.trim(),
            min = min,
            max = max,
            increment = inc,
            current = current,
            colorArgb = colorArgb
        ) ?: GaugeEntry(
            id = Uuid.random().toString(),
            label = label.trim(),
            min = min,
            max = max,
            increment = inc,
            current = current,
            colorArgb = colorArgb
        )
    }
}

@OptIn(ExperimentalUuidApi::class)
class DraftCharacteristic(val origin: CharacteristicEntry? = null) {
    val key: String = origin?.id ?: Uuid.random().toString()
    var label by mutableStateOf(origin?.label ?: "")
    var value by mutableStateOf(origin?.value?.toString() ?: "")
    var description by mutableStateOf(origin?.description.orEmpty())

    /**
     * La description compte : une ligne où elle serait seule renseignée ne doit pas être écartée
     * silencieusement par [CharacterSheetEditorState.validateAndBuild], mais signalée comme invalide.
     */
    fun isEmpty(): Boolean = label.isBlank() && value.isBlank() && description.isBlank()

    fun isValid(): Boolean {
        val parsed = value.toLongOrNull() ?: return false
        return CharacterSheetValidator.validateCharacteristic(label, parsed, description).isSuccess
    }

    fun toEntry(): CharacteristicEntry {
        val parsed = value.toLong()
        return origin?.copy(
            label = label.trim(),
            value = parsed,
            description = description.trimmedOrNull()
        ) ?: CharacteristicEntry(
            id = Uuid.random().toString(),
            label = label.trim(),
            value = parsed,
            description = description.trimmedOrNull()
        )
    }
}

@OptIn(ExperimentalUuidApi::class)
class DraftSkill(val origin: SkillEntry? = null) {
    val key: String = origin?.id ?: Uuid.random().toString()
    var label by mutableStateOf(origin?.label ?: "")
    var description by mutableStateOf(origin?.description.orEmpty())

    fun isEmpty(): Boolean = label.isBlank() && description.isBlank()

    fun isValid(): Boolean = CharacterSheetValidator.validateSkill(label, description).isSuccess

    fun toEntry(): SkillEntry = origin?.copy(
        label = label.trim(),
        description = description.trimmedOrNull()
    ) ?: SkillEntry(
        id = Uuid.random().toString(),
        label = label.trim(),
        description = description.trimmedOrNull()
    )
}

@OptIn(ExperimentalUuidApi::class)
class DraftPower(val origin: PowerEntry? = null) {
    val key: String = origin?.id ?: Uuid.random().toString()
    var label by mutableStateOf(origin?.label ?: "")
    var description by mutableStateOf(origin?.description.orEmpty())

    fun isEmpty(): Boolean = label.isBlank() && description.isBlank()

    fun isValid(): Boolean = CharacterSheetValidator.validatePower(label, description).isSuccess

    fun toEntry(): PowerEntry = origin?.copy(
        label = label.trim(),
        description = description.trimmedOrNull()
    ) ?: PowerEntry(
        id = Uuid.random().toString(),
        label = label.trim(),
        description = description.trimmedOrNull()
    )
}

/** Une description vide n'est jamais persistée : `null` ⇒ aucune ligne à l'affichage. */
private fun String.trimmedOrNull(): String? = trim().takeIf(String::isNotBlank)

// --- État de l'éditeur ---

/**
 * [allowInitialEdit] : autorise la saisie de la valeur initiale des jauges **déjà existantes**
 * (éditeur de pré-tirés). À `false` (fiche du joueur), seules les jauges ajoutées pendant la session
 * d'édition proposent ce champ.
 */
class CharacterSheetEditorState(
    initial: CharacterSheet,
    private val allowInitialEdit: Boolean = false
) {
    val gauges = mutableStateListOf<DraftGauge>().apply {
        addAll(initial.gauges.map { DraftGauge(it, allowInitialEdit) })
    }
    val characteristics = mutableStateListOf<DraftCharacteristic>().apply {
        addAll(initial.characteristics.map { DraftCharacteristic(it) })
    }
    val skills = mutableStateListOf<DraftSkill>().apply {
        addAll(initial.skills.map { DraftSkill(it) })
    }
    val powers = mutableStateListOf<DraftPower>().apply {
        addAll(initial.powers.map { DraftPower(it) })
    }

    /** Passe à `true` après une tentative d'enregistrement échouée ⇒ surligne les lignes fautives. */
    var showErrors by mutableStateOf(false)
        private set

    /**
     * Clé de la ligne qui vient d'être ajoutée : son champ « nom » prend le focus dès son apparition
     * (évite à l'utilisateur de cliquer dedans), puis la clé est consommée pour ne pas re-focaliser
     * la ligne si l'éditeur est recomposé plus tard.
     */
    var pendingFocusKey by mutableStateOf<String?>(null)
        private set

    fun consumePendingFocus() {
        pendingFocusKey = null
    }

    fun addGauge() {
        val draft = DraftGauge(allowInitialEdit = allowInitialEdit)
        gauges.add(draft)
        pendingFocusKey = draft.key
    }
    fun removeGauge(draft: DraftGauge) = gauges.remove(draft)

    fun addCharacteristic() {
        val draft = DraftCharacteristic()
        characteristics.add(draft)
        pendingFocusKey = draft.key
    }
    fun removeCharacteristic(draft: DraftCharacteristic) = characteristics.remove(draft)

    fun addSkill() {
        val draft = DraftSkill()
        skills.add(draft)
        pendingFocusKey = draft.key
    }
    fun removeSkill(draft: DraftSkill) = skills.remove(draft)

    fun addPower() {
        val draft = DraftPower()
        powers.add(draft)
        pendingFocusKey = draft.key
    }
    fun removePower(draft: DraftPower) = powers.remove(draft)

    /** Remplace tout le brouillon par le contenu de [sheet] (p. ex. copie depuis un autre pré-tiré). */
    fun load(sheet: CharacterSheet) {
        gauges.clear(); gauges.addAll(sheet.gauges.map { DraftGauge(it, allowInitialEdit) })
        characteristics.clear(); characteristics.addAll(sheet.characteristics.map { DraftCharacteristic(it) })
        skills.clear(); skills.addAll(sheet.skills.map { DraftSkill(it) })
        powers.clear(); powers.addAll(sheet.powers.map { DraftPower(it) })
        pendingFocusKey = null
        showErrors = false
    }

    /**
     * Écarte les lignes entièrement vides, valide le reste et renvoie le [CharacterSheet] correspondant.
     * Renvoie `null` (et active [showErrors]) si une ligne non-vide est invalide.
     */
    fun validateAndBuild(): CharacterSheet? {
        val g = gauges.filterNot { it.isEmpty() }
        val c = characteristics.filterNot { it.isEmpty() }
        val s = skills.filterNot { it.isEmpty() }
        val p = powers.filterNot { it.isEmpty() }
        if (g.any { !it.isValid() } || c.any { !it.isValid() } || s.any { !it.isValid() } ||
            p.any { !it.isValid() }
        ) {
            showErrors = true
            return null
        }
        showErrors = false
        return CharacterSheet(
            gauges = g.map { it.toEntry() },
            characteristics = c.map { it.toEntry() },
            skills = s.map { it.toEntry() },
            powers = p.map { it.toEntry() }
        )
    }
}

@Composable
fun rememberCharacterSheetEditorState(
    initial: CharacterSheet,
    resetKey: Any?,
    allowInitialEdit: Boolean = false
): CharacterSheetEditorState =
    remember(resetKey) { CharacterSheetEditorState(initial, allowInitialEdit) }

// --- UI ---

@Composable
fun CharacterSheetEditor(
    state: CharacterSheetEditorState,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        EditorSection(
            icon = Icons.Default.Favorite,
            title = stringResource(Res.string.character_sheet_gauges_section)
        ) {
            state.gauges.forEach { draft ->
                key(draft.key) {
                    GaugeEditRow(
                        draft = draft,
                        enabled = enabled,
                        showError = state.showErrors && draft.isInvalidNonEmpty(),
                        onDelete = { state.removeGauge(draft) },
                        nameFocusRequester = rememberNewEntryFocusRequester(state, draft.key)
                    )
                }
            }
            AddEntryButton(
                label = stringResource(Res.string.character_sheet_add_gauge),
                enabled = enabled,
                onClick = state::addGauge
            )
        }

        EditorSection(
            icon = Icons.Default.Shield,
            title = stringResource(Res.string.character_sheet_characteristics_section)
        ) {
            if (state.characteristics.isNotEmpty()) {
                OutlinedCard(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp)) {
                    state.characteristics.forEachIndexed { index, draft ->
                        key(draft.key) {
                            CharacteristicEditRow(
                                draft = draft,
                                enabled = enabled,
                                showError = state.showErrors && draft.isInvalidNonEmpty(),
                                onDelete = { state.removeCharacteristic(draft) },
                                nameFocusRequester = rememberNewEntryFocusRequester(state, draft.key),
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp)
                            )
                        }
                        if (index < state.characteristics.lastIndex) {
                            HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                        }
                    }
                }
            }
            AddEntryButton(
                label = stringResource(Res.string.character_sheet_add_characteristic),
                enabled = enabled,
                onClick = state::addCharacteristic
            )
        }

        EditorSection(
            icon = Icons.AutoMirrored.Filled.MenuBook,
            title = stringResource(Res.string.character_sheet_skills_section)
        ) {
            state.skills.forEachIndexed { index, draft ->
                key(draft.key) {
                    SkillEditRow(
                        draft = draft,
                        enabled = enabled,
                        showError = state.showErrors && draft.isInvalidNonEmpty(),
                        onDelete = { state.removeSkill(draft) },
                        nameFocusRequester = rememberNewEntryFocusRequester(state, draft.key)
                    )
                }
                if (index < state.skills.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                }
            }
            AddEntryButton(
                label = stringResource(Res.string.character_sheet_add_skill),
                enabled = enabled,
                onClick = state::addSkill
            )
        }

        EditorSection(
            icon = Icons.Default.Whatshot,
            title = stringResource(Res.string.character_sheet_powers_section)
        ) {
            state.powers.forEachIndexed { index, draft ->
                key(draft.key) {
                    PowerEditRow(
                        draft = draft,
                        enabled = enabled,
                        showError = state.showErrors && draft.isInvalidNonEmpty(),
                        onDelete = { state.removePower(draft) },
                        nameFocusRequester = rememberNewEntryFocusRequester(state, draft.key)
                    )
                }
                if (index < state.powers.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                }
            }
            AddEntryButton(
                label = stringResource(Res.string.character_sheet_add_power),
                enabled = enabled,
                onClick = state::addPower
            )
        }
    }
}

/**
 * [FocusRequester] du champ « nom » d'une ligne : si la ligne vient d'être ajoutée
 * ([CharacterSheetEditorState.pendingFocusKey]), le focus y est placé dès qu'elle est composée.
 */
@Composable
private fun rememberNewEntryFocusRequester(
    state: CharacterSheetEditorState,
    key: String
): FocusRequester {
    val focusRequester = remember { FocusRequester() }
    LaunchedEffect(Unit) {
        if (state.pendingFocusKey == key) {
            focusRequester.requestFocus()
            state.consumePendingFocus()
        }
    }
    return focusRequester
}

private fun DraftGauge.isInvalidNonEmpty() = !isEmpty() && !isValid()
private fun DraftCharacteristic.isInvalidNonEmpty() = !isEmpty() && !isValid()
private fun DraftSkill.isInvalidNonEmpty() = !isEmpty() && !isValid()
private fun DraftPower.isInvalidNonEmpty() = !isEmpty() && !isValid()

@Composable
private fun EditorSection(
    icon: ImageVector,
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    OutlinedCard(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp)) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = title.uppercase(),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 1.5.sp,
                    color = MaterialTheme.colorScheme.primary
                )
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(1.dp)
                        .background(
                            Brush.horizontalGradient(
                                listOf(
                                    MaterialTheme.colorScheme.primary.copy(alpha = 0.3f),
                                    Color.Transparent
                                )
                            )
                        )
                )
            }
            content()
        }
    }
}

@Composable
private fun GaugeEditRow(
    draft: DraftGauge,
    enabled: Boolean,
    showError: Boolean,
    onDelete: () -> Unit,
    nameFocusRequester: FocusRequester
) {
    var showColorPicker by remember { mutableStateOf(false) }
    val defaultColor = MaterialTheme.colorScheme.primary
    val color = draft.colorArgb?.let { Color(it) } ?: defaultColor

    Column(
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(vertical = 4.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            EditorTextField(
                value = draft.label,
                onValueChange = {
                    if (it.length <= CharacterSheetValidator.MAX_GAUGE_LABEL_LENGTH) draft.label = it
                },
                label = stringResource(Res.string.character_sheet_gauge_name),
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f).focusRequester(nameFocusRequester)
            )
            DeleteEntryButton(enabled = enabled, onDelete = onDelete)
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            EditorNumberField(
                value = draft.start,
                onValueChange = { draft.start = it },
                label = stringResource(Res.string.character_sheet_gauge_start),
                maxDigits = 6,
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f)
            )
            EditorNumberField(
                value = draft.end,
                onValueChange = { draft.end = it },
                label = stringResource(Res.string.character_sheet_gauge_end),
                maxDigits = 6,
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f)
            )
            EditorNumberField(
                value = draft.increment,
                onValueChange = { draft.increment = it },
                label = stringResource(Res.string.character_sheet_gauge_increment),
                maxDigits = 6,
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f)
            )
        }
        if (draft.showInitial) {
            EditorNumberField(
                value = draft.initial,
                onValueChange = { draft.initial = it },
                label = stringResource(Res.string.character_sheet_gauge_initial),
                maxDigits = 6,
                enabled = enabled,
                isError = showError,
                modifier = Modifier.fillMaxWidth()
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(
                text = stringResource(Res.string.character_sheet_gauge_color),
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(CircleShape)
                    .background(color)
                    .then(
                        if (enabled) Modifier.clickable { showColorPicker = true }
                        else Modifier
                    )
            )
            TextButton(onClick = { showColorPicker = true }, enabled = enabled) {
                Text(stringResource(Res.string.character_sheet_gauge_color_pick))
            }
        }
    }

    if (showColorPicker) {
        AlertDialog(
            onDismissRequest = { showColorPicker = false },
            title = { Text(stringResource(Res.string.character_sheet_gauge_color_pick)) },
            text = {
                HsvColorPicker(
                    color = color,
                    onColorChanged = { draft.colorArgb = it.toArgbLong() },
                    modifier = Modifier.fillMaxWidth()
                )
            },
            confirmButton = {
                TextButton(onClick = { showColorPicker = false }) {
                    Text(stringResource(Res.string.character_sheet_add))
                }
            },
            dismissButton = {
                TextButton(onClick = { showColorPicker = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

@Composable
private fun CharacteristicEditRow(
    draft: DraftCharacteristic,
    enabled: Boolean,
    showError: Boolean,
    onDelete: () -> Unit,
    nameFocusRequester: FocusRequester,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.padding(vertical = 4.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            EditorTextField(
                value = draft.label,
                onValueChange = {
                    if (it.length <= CharacterSheetValidator.MAX_CHARACTERISTIC_LABEL_LENGTH) draft.label = it
                },
                label = stringResource(Res.string.character_sheet_characteristic_name),
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f).focusRequester(nameFocusRequester)
            )
            EditorNumberField(
                value = draft.value,
                onValueChange = { draft.value = it },
                label = stringResource(Res.string.character_sheet_characteristic_value),
                maxDigits = CharacterSheetValidator.MAX_CHARACTERISTIC_VALUE_DIGITS,
                enabled = enabled,
                isError = showError,
                modifier = Modifier.width(110.dp)
            )
            DeleteEntryButton(enabled = enabled, onDelete = onDelete)
        }
        EditorDescriptionField(
            value = draft.description,
            onValueChange = { draft.description = it },
            enabled = enabled,
            isError = showError
        )
    }
}

@Composable
private fun SkillEditRow(
    draft: DraftSkill,
    enabled: Boolean,
    showError: Boolean,
    onDelete: () -> Unit,
    nameFocusRequester: FocusRequester
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            EditorTextField(
                value = draft.label,
                onValueChange = {
                    if (it.length <= CharacterSheetValidator.MAX_SKILL_LABEL_LENGTH) draft.label = it
                },
                label = stringResource(Res.string.character_sheet_skill_label),
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f).focusRequester(nameFocusRequester)
            )
            DeleteEntryButton(enabled = enabled, onDelete = onDelete)
        }
        EditorDescriptionField(
            value = draft.description,
            onValueChange = { draft.description = it },
            enabled = enabled,
            isError = showError
        )
    }
}

@Composable
private fun PowerEditRow(
    draft: DraftPower,
    enabled: Boolean,
    showError: Boolean,
    onDelete: () -> Unit,
    nameFocusRequester: FocusRequester
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            EditorTextField(
                value = draft.label,
                onValueChange = {
                    if (it.length <= CharacterSheetValidator.MAX_POWER_LABEL_LENGTH) draft.label = it
                },
                label = stringResource(Res.string.character_sheet_power_label),
                enabled = enabled,
                isError = showError,
                modifier = Modifier.weight(1f).focusRequester(nameFocusRequester)
            )
            DeleteEntryButton(enabled = enabled, onDelete = onDelete)
        }
        EditorDescriptionField(
            value = draft.description,
            onValueChange = { draft.description = it },
            enabled = enabled,
            isError = showError
        )
    }
}

@Composable
private fun AddEntryButton(label: String, enabled: Boolean, onClick: () -> Unit) {
    TextButton(onClick = onClick, enabled = enabled) {
        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
        Text(label, modifier = Modifier.padding(start = 8.dp))
    }
}

@Composable
private fun DeleteEntryButton(enabled: Boolean, onDelete: () -> Unit) {
    IconButton(onClick = onDelete, modifier = Modifier.size(32.dp), enabled = enabled) {
        Icon(
            imageVector = Icons.Default.Delete,
            contentDescription = stringResource(Res.string.character_sheet_delete_entry),
            tint = if (enabled) MaterialTheme.colorScheme.error
            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f),
            modifier = Modifier.size(20.dp)
        )
    }
}

@Composable
private fun EditorTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    enabled: Boolean,
    isError: Boolean,
    modifier: Modifier = Modifier
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        singleLine = true,
        enabled = enabled,
        isError = isError,
        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
        modifier = modifier
    )
}

/** Champ multiligne facultatif, borné à [CharacterSheetValidator.MAX_ENTRY_DESCRIPTION_LENGTH]. */
@Composable
private fun EditorDescriptionField(
    value: String,
    onValueChange: (String) -> Unit,
    enabled: Boolean,
    isError: Boolean,
    modifier: Modifier = Modifier
) {
    OutlinedTextField(
        value = value,
        onValueChange = {
            if (it.length <= CharacterSheetValidator.MAX_ENTRY_DESCRIPTION_LENGTH) onValueChange(it)
        },
        label = { Text(stringResource(Res.string.character_sheet_entry_description)) },
        singleLine = false,
        minLines = 2,
        maxLines = 4,
        enabled = enabled,
        isError = isError,
        supportingText = {
            Text("${value.length}/${CharacterSheetValidator.MAX_ENTRY_DESCRIPTION_LENGTH}")
        },
        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
        modifier = modifier.fillMaxWidth()
    )
}

@Composable
private fun EditorNumberField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    maxDigits: Int,
    enabled: Boolean,
    isError: Boolean,
    modifier: Modifier = Modifier
) {
    OutlinedTextField(
        value = value,
        onValueChange = { input ->
            if (input.all(Char::isDigit) && input.length <= maxDigits) onValueChange(input)
        },
        label = { Text(label) },
        singleLine = true,
        enabled = enabled,
        isError = isError,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        modifier = modifier
    )
}

/** Convertit une [Color] Compose en `Long` ARGB positif (0xAARRGGBB), stockable en jsonb. */
private fun Color.toArgbLong(): Long = toArgb().toLong() and 0xFFFFFFFFL
